// Reusable Apple "Liquid Glass" surface (iOS 26 / macOS Tahoe look).
//
// Layers: glassmorphism (backdrop blur) + optional SVG feDisplacementMap
// refraction + specular edge highlight. SVG-displacement refraction is solid in
// Chromium but only partial in Safari/Firefox, so we feature-detect and degrade
// to plain frosted glass there. Respects prefers-reduced-transparency.
import { useId, type PropsWithChildren, type CSSProperties } from "react";

function supportsBackdropFilter(): boolean {
  if (typeof CSS === "undefined" || !CSS.supports) return false;
  return (
    CSS.supports("backdrop-filter", "blur(1px)") ||
    CSS.supports("-webkit-backdrop-filter", "blur(1px)")
  );
}

// Chromium composites SVG filters referenced from backdrop-filter; others vary.
const REFRACTION_OK =
  typeof navigator !== "undefined" && /Chrome|Chromium|Edg/.test(navigator.userAgent);

interface GlassSurfaceProps {
  className?: string;
  style?: CSSProperties;
  /** Disable the heavier refraction layer (e.g. for many small widgets). */
  refraction?: boolean;
  radius?: number;
}

export function GlassSurface({
  children,
  className = "",
  style,
  refraction = true,
  radius = 20,
}: PropsWithChildren<GlassSurfaceProps>) {
  const filterId = useId().replace(/:/g, "");
  const useRefraction = refraction && REFRACTION_OK;
  const blur = supportsBackdropFilter() ? "blur(14px) saturate(160%)" : undefined;

  const backdrop = useRefraction
    ? `url(#glass-${filterId}) ${blur ?? ""}`.trim()
    : blur;

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{
        borderRadius: radius,
        background: "rgba(255,255,255,0.10)",
        border: "1px solid rgba(255,255,255,0.18)",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.35), 0 8px 30px rgba(0,0,0,0.25)",
        backdropFilter: backdrop,
        WebkitBackdropFilter: blur,
        ...style,
      }}
    >
      {useRefraction && (
        <svg width="0" height="0" aria-hidden style={{ position: "absolute" }}>
          <filter id={`glass-${filterId}`} x="0" y="0" width="100%" height="100%">
            {/* Generate a soft noise field, then warp the backdrop by it. */}
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.008 0.012"
              numOctaves={2}
              seed={7}
              result="noise"
            />
            <feGaussianBlur in="noise" stdDeviation="2" result="blurredNoise" />
            <feDisplacementMap
              in="SourceGraphic"
              in2="blurredNoise"
              scale={22}
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </svg>
      )}
      {/* Specular highlight overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          borderRadius: radius,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 40%)",
          mixBlendMode: "screen",
          opacity: 0.5,
        }}
      />
      <div className="relative">{children}</div>
    </div>
  );
}
